import { API_ENDPOINTS } from './api';

export const DEFAULT_AVATAR =
  'https://ui-avatars.com/api/?background=1a365d&color=fff&bold=true';

export const STATUS_META = {
  moving: {
    label: 'Moving',
    color: 'bg-blue-500',
    badge: 'bg-blue-50 text-blue-700',
    dot: 'bg-blue-500 animate-pulse',
  },
  'in-office': {
    label: 'In office',
    color: 'bg-teal-500',
    badge: 'bg-teal-50 text-teal-700',
    dot: 'bg-teal-500',
  },
  field: {
    label: 'On field',
    color: 'bg-amber-500',
    badge: 'bg-amber-50 text-amber-700',
    dot: 'bg-amber-500',
  },
  'checked-out': {
    label: 'Checked out',
    color: 'bg-slate-400',
    badge: 'bg-slate-100 text-slate-600',
    dot: 'bg-slate-400',
  },
  offline: {
    label: 'Offline',
    color: 'bg-rose-400',
    badge: 'bg-rose-50 text-rose-700',
    dot: 'bg-rose-400',
  },
  'no-location': {
    label: 'No GPS',
    color: 'bg-slate-300',
    badge: 'bg-slate-100 text-slate-500',
    dot: 'bg-slate-300',
  },
};

export const resolveAvatarUrl = (profilePic, name = 'User') => {
  if (!profilePic) {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=1a365d&color=fff&bold=true`;
  }
  if (profilePic.startsWith('http')) return profilePic;
  return `${API_ENDPOINTS.uploadPath}/${profilePic.split(/[\\/]/).pop()}`;
};

export const getDepartmentOptions = (employees = []) => {
  const set = new Set();
  employees.forEach((emp) => {
    if (emp.department?.trim()) set.add(emp.department.trim());
  });
  return ['all', ...Array.from(set).sort()];
};

export const buildLocationStats = (employees = []) => {
  const stats = {
    total: employees.length,
    onMap: 0,
    moving: 0,
    inOffice: 0,
    field: 0,
    checkedOut: 0,
    offline: 0,
  };

  employees.forEach((emp) => {
    if (emp.hasLocation) stats.onMap += 1;
    if (emp.status === 'moving') stats.moving += 1;
    if (emp.status === 'in-office') stats.inOffice += 1;
    if (emp.status === 'field') stats.field += 1;
    if (emp.status === 'checked-out') stats.checkedOut += 1;
    if (emp.status === 'offline' || emp.status === 'no-location') stats.offline += 1;
  });

  return stats;
};

export const getMapCenter = (offices = [], employees = []) => {
  if (offices.length > 0) {
    const lat = offices.reduce((sum, o) => sum + o.latitude, 0) / offices.length;
    const lng = offices.reduce((sum, o) => sum + o.longitude, 0) / offices.length;
    return { lat, lng };
  }

  const located = employees.filter((e) => e.latitude != null && e.longitude != null);
  if (located.length > 0) {
    const lat = located.reduce((sum, e) => sum + e.latitude, 0) / located.length;
    const lng = located.reduce((sum, e) => sum + e.longitude, 0) / located.length;
    return { lat, lng };
  }

  return { lat: 12.9716, lng: 80.2202 };
};

export const formatLastUpdate = (timestamp) => {
  if (!timestamp) return 'No update today';
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Online = active session, live GPS, or checked in today */
export const getOnlineStatus = (employee) => {
  const isOnline =
    employee?.isOnline === true ||
    employee?.isSessionOnline === true ||
    employee?.isLive === true ||
    employee?.lastType === 'check-in';

  if (isOnline) {
    return {
      isOnline: true,
      label: 'Online',
      badge: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      dot: 'bg-emerald-500 animate-pulse',
      ring: 'ring-emerald-400',
    };
  }

  return {
    isOnline: false,
    label: 'Offline',
    badge: 'bg-slate-100 text-slate-600 border border-slate-200',
    dot: 'bg-slate-400',
    ring: 'ring-slate-300',
  };
};

export const formatCoords = (lat, lng) => {
  if (lat == null || lng == null || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) {
    return null;
  }
  return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
};

export const formatAccuracy = (meters) => {
  if (!Number.isFinite(meters)) return null;
  if (meters < 1) return '±1 m';
  if (meters < 1000) return `±${Math.round(meters)} m`;
  return `±${(meters / 1000).toFixed(1)} km`;
};

/** Place every employee with real GPS coordinates on the map (live + last-known). */
export const prepareEmployeesForMap = (employees = []) =>
  employees
    .map((emp) => ({
      ...emp,
      latitude: Number(emp.latitude),
      longitude: Number(emp.longitude),
    }))
    .filter((emp) => Number.isFinite(emp.latitude) && Number.isFinite(emp.longitude));

/** One row per person — same email (or userId) must not appear twice. */
export const uniqueEmployeesByIdentity = (employees = []) => {
  const byKey = new Map();

  employees.forEach((emp) => {
    const key = (emp.email || '').trim().toLowerCase() || String(emp.userId);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, emp);
      return;
    }

    const preferNext =
      (emp.hasLocation && !existing.hasLocation) ||
      (emp.hasLocation === existing.hasLocation &&
        new Date(emp.lastUpdate || 0).getTime() > new Date(existing.lastUpdate || 0).getTime());
    const keep = preferNext ? emp : existing;
    const drop = preferNext ? existing : emp;

    byKey.set(key, {
      ...drop,
      ...keep,
      name: keep.name || drop.name,
      profilePic: keep.profilePic || drop.profilePic,
      department: keep.department || drop.department,
      position: keep.position && keep.position !== 'Master Admin' ? keep.position : drop.position || keep.position,
      employeeId: keep.employeeId || drop.employeeId,
      hasLocation: Boolean(keep.hasLocation || drop.hasLocation),
      latitude: keep.hasLocation ? keep.latitude : drop.latitude,
      longitude: keep.hasLocation ? keep.longitude : drop.longitude,
      isOnline: keep.isOnline || drop.isOnline,
      isLive: keep.isLive || drop.isLive,
    });
  });

  return Array.from(byKey.values());
};

export const buildLocationStatsFromMap = (employees = [], mapEmployees = []) => {
  const base = buildLocationStats(employees);
  const onlineCount = employees.filter((e) => getOnlineStatus(e).isOnline).length;
  return {
    ...base,
    online: onlineCount,
    offline: employees.length - onlineCount,
    onMap: mapEmployees.length,
  };
};
